import {
  ConnectionTreeNode,
  DatabaseTreeNode,
  DatabasesFolderNode,
  TablesFolderNode,
  ViewsFolderNode,
  ProgrammabilityFolderNode,
  StoredProceduresFolderNode,
  FunctionsFolderNode,
  FolderTreeNode,
  DatabaseObjectTreeNode,
  MessageTreeNode,
  ColumnsFolderNode,
  ColumnTreeNode,
  TableSubfolderNode,
  createDatabasesFolderNode,
  createTablesFolderNode,
  createViewsFolderNode,
  createProgrammabilityFolderNode,
  isDatabaseNode,
  isTablesFolderNode,
  isViewsFolderNode,
  isProgrammabilityFolderNode,
  isStoredProceduresFolderNode,
  isFunctionsFolderNode,
  isDatabasesFolderNode,
  isColumnsFolderNode,
  isTableSubfolderNode,
  isColumnNode,
  DatabaseExplorerNode
} from './DatabaseTreeModels';
import { DatabaseObjectType } from '../../database-objects/models/databaseObjectTypes';
import { DatabaseEngineId } from '../../../core/engines/engineTypes';
import { displayColumnLength, isLengthDataType } from '../../../core/engines/columnLength';
import {
  createMssqlServerSecurityFolders,
  createPgsqlServerSecurityFolders,
  createOracleServerSecurityFolders,
} from './securityNodeFactories';
import { createJobSchedulerFolders } from './agentNodeFactories';
import { buildPinnedChildren } from '../../database-objects/services/pinnedObjects/pinnedChildrenBuilder';
import { usePinnedObjectsStore } from '../../database-objects/store/pinnedObjectsStore';
import { getStorageAccountInfoSnapshot } from '../../database-objects/store/storageAccountInfoStore';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import { buildStorageConnectionChildren } from './azureStorageNodeFactories';

/**
 * Connection node behaviors
 */
export const ConnectionNodeBehaviors = {
  /**
   * Initializes a connection node with its standard folders
   * Includes engine-specific security folders when engine is provided
   */
  initializeDefaultStructure(node: ConnectionTreeNode, engine?: DatabaseEngineId): DatabaseExplorerNode[] {
    // Pinned wrappers come first; reads the latest store snapshot so freshly-built
    // connection nodes already include pins when the store is hydrated. The store
    // never throws when the IPC is missing — it falls back to an empty list — so
    // this is safe in non-renderer contexts (e.g. test harnesses).
    //
    // Note: `buildPinnedChildren` is intentionally invoked WITHOUT a queryClient
    // here. This call runs at connection-add time, before any `database-objects`
    // listing has been fetched — so the cache is empty and every node would
    // resolve to `unresolved` anyway (which is the factory default). The live
    // upgrade to `resolved` / `stale` happens in the subscriber inside
    // `DatabaseExplorer.tsx`, which rebuilds pinned children with the live
    // `useQueryClient()` whenever the pinned list changes (and after the
    // databases query has populated the cache).
    const connectionId = node.metadata.id;
    const pins =
      usePinnedObjectsStore.getState().byConnection[connectionId]?.pinnedObjects ?? [];
    const pinnedChildren = buildPinnedChildren({
      connectionId,
      parentId: node.id,
      pins,
      engine: engine ?? 'mssql',
    });

    // Azure Storage (spec §5.1): pinned run + one service folder per
    // implemented, enabled, reachable service — no Databases / security / job
    // folders. Scope, enabled services and the account-info snapshot are read
    // synchronously from their stores; `useStorageAccountInfoSync` rebuilds the
    // branch when the probe answers after connect.
    if (engine === 'azurestorage') {
      const storageChildren = buildStorageConnectionChildren({
        connectionId,
        parentId: node.id,
        connection: getConnectionById(connectionId).connection,
        accountInfo: getStorageAccountInfoSnapshot(connectionId),
      });
      return [...pinnedChildren, ...storageChildren];
    }

    // Spec §Connection lifecycle: when the connection has ≥1 pin, the pinned
    // area is the intended entry point — Databases stays collapsed on first
    // build so it doesn't push pinned rows out of view. With zero pins, the
    // existing auto-expand is unchanged.
    const databasesFolder = createDatabasesFolderNode(connectionId, engine);
    if (pins.length > 0) {
      databasesFolder.isExpanded = false;
    }
    const folders: FolderTreeNode[] = [databasesFolder];

    // Add engine-specific security folders
    if (engine === 'mssql' || engine === 'mock') {
      // Mock engine uses MSSQL-style security folders for testing
      folders.push(...createMssqlServerSecurityFolders(connectionId));
    } else if (engine === 'pgsql') {
      folders.push(...createPgsqlServerSecurityFolders(connectionId));
    } else if (engine === 'oracle') {
      folders.push(...createOracleServerSecurityFolders(connectionId));
    }

    // Add job-scheduler folder for engines that support it (engine-neutral)
    const jobSchedulerFolders = createJobSchedulerFolders(connectionId, engine);
    if (jobSchedulerFolders.length > 0) {
      folders.push(...jobSchedulerFolders);
    }

    return [...pinnedChildren, ...folders];
  },
  
  /**
   * Gets connection status info
   */
  getStatusInfo(node: ConnectionTreeNode): { isConnected: boolean; lastConnected?: Date } {
    return {
      isConnected: node.metadata?.status === 'connected',
      lastConnected: node.metadata?.connectedAt
    };
  },
  
  /**
   * Creates connection string for display
   */
  getConnectionString(node: ConnectionTreeNode): string {
    const { url, port, username } = node.metadata;
    return `${username}@${url}${port ? ':' + port : ''}`;
  }
};

/**
 * Database node behaviors
 */
export const DatabaseNodeBehaviors = {
  /**
   * Initializes a database node with its standard folders
   */
  initializeDefaultStructure(node: DatabaseTreeNode): FolderTreeNode[] {
    const { connectionId, name: databaseName } = node.metadata;
    
    // Create the standard folders for database objects
    return [
      createTablesFolderNode(connectionId, databaseName),
      createViewsFolderNode(connectionId, databaseName),
      createProgrammabilityFolderNode(connectionId, databaseName)
    ];
  },
  
  /**
   * Gets the appropriate folder for a database object type
   */
  getFolderForObjectType(
    node: DatabaseTreeNode, 
    objectType: DatabaseObjectType
  ): FolderTreeNode | undefined {
    // Find the appropriate folder for this object type
    if (objectType === 'table') {
      return node.children?.find(isTablesFolderNode) as TablesFolderNode | undefined;
    } else if (objectType === 'view') {
      return node.children?.find(isViewsFolderNode) as ViewsFolderNode | undefined;
    } else if (objectType === 'stored-procedure') {
      // For stored procedures, need to navigate through programmability folder
      const progFolder = node.children?.find(isProgrammabilityFolderNode) as ProgrammabilityFolderNode | undefined;
      return progFolder?.children?.find(isStoredProceduresFolderNode) as StoredProceduresFolderNode | undefined;
    } else if (objectType === 'function') {
      // For functions, need to navigate through programmability folder
      const progFolder = node.children?.find(isProgrammabilityFolderNode) as ProgrammabilityFolderNode | undefined;
      return progFolder?.children?.find(isFunctionsFolderNode) as FunctionsFolderNode | undefined;
    }
    
    return undefined;
  }
};

/**
 * Databases folder behaviors
 */
export const DatabasesFolderBehaviors = {
  /**
   * Checks if the folder is empty
   */
  isEmpty(node: DatabasesFolderNode): boolean {
    return !node.children || node.children.length === 0;
  },
  
  /**
   * Gets folder content statistics
   */
  getContentStats(node: DatabasesFolderNode): { count: number; hasErrors: boolean } {
    const childCount = node.children?.length || 0;
    const hasErrors = node.children?.some(child => 
      child.type === 'error' || child.metadata?.status === 'error'
    ) || false;
    
    return { count: childCount, hasErrors };
  }
};

/**
 * Tables folder behaviors
 */
export const TablesFolderBehaviors = {
  /**
   * Refreshes the list of tables from the database
   */
  refreshTables(node: TablesFolderNode): Promise<void> {
    // This would be implemented to fetch tables from database
    void node;
    return Promise.resolve();
  },
  
  /**
   * Gets folder content statistics
   */
  getContentStats(node: TablesFolderNode): { count: number } {
    return { count: node.children?.length || 0 };
  }
};

/**
 * Views folder behaviors
 */
export const ViewsFolderBehaviors = {
  /**
   * Refreshes the list of views from the database
   */
  refreshViews(node: ViewsFolderNode): Promise<void> {
    // This would be implemented to fetch views from database
    void node;
    return Promise.resolve();
  },
  
  /**
   * Gets folder content statistics
   */
  getContentStats(node: ViewsFolderNode): { count: number } {
    return { count: node.children?.length || 0 };
  }
};

/**
 * Programmability folder behaviors
 */
export const ProgrammabilityFolderBehaviors = {
  /**
   * Gets subfolder counts
   */
  getSubfolderCounts(node: ProgrammabilityFolderNode): { procedures: number; functions: number } {
    const spFolder = node.children?.find(isStoredProceduresFolderNode);
    const fnFolder = node.children?.find(isFunctionsFolderNode);
    
    return {
      procedures: spFolder?.children?.length || 0,
      functions: fnFolder?.children?.length || 0
    };
  }
};

/**
 * Stored procedures folder behaviors
 */
export const StoredProceduresFolderBehaviors = {
  /**
   * Refreshes the list of stored procedures from the database
   */
  refreshProcedures(node: StoredProceduresFolderNode): Promise<void> {
    // This would be implemented to fetch stored procedures from database
    void node;
    return Promise.resolve();
  },
  
  /**
   * Gets folder content statistics
   */
  getContentStats(node: StoredProceduresFolderNode): { count: number } {
    return { count: node.children?.length || 0 };
  }
};

/**
 * Functions folder behaviors
 */
export const FunctionsFolderBehaviors = {
  /**
   * Refreshes the list of functions from the database
   */
  refreshFunctions(node: FunctionsFolderNode): Promise<void> {
    // This would be implemented to fetch functions from database
    void node;
    return Promise.resolve();
  },
  
  /**
   * Gets folder content statistics
   */
  getContentStats(node: FunctionsFolderNode): { count: number } {
    return { count: node.children?.length || 0 };
  }
};

/**
 * Database object node behaviors
 */
export const DatabaseObjectNodeBehaviors = {
  /**
   * Gets schema-qualified name
   */
  getFullyQualifiedName(node: DatabaseObjectTreeNode): string {
    const { objectDetails } = node.metadata;
    return objectDetails.schema 
      ? `${objectDetails.schema}.${objectDetails.name}`
      : objectDetails.name;
  },
  
  /**
   * Gets the object's icon
   */
  getIconName(node: DatabaseObjectTreeNode): string {
    // Map object types to icon names
    const iconMap: Record<string, string> = {
      'table': 'table',
      'view': 'view',
      'stored-procedure': 'stored-procedure',
      'function': 'function',
      'package': 'package',
      'sequence': 'sequence',
      'synonym': 'synonym',
      'db-link': 'db-link',
      'materialized-view': 'materialized-view',
      'type': 'type',
      'directory': 'directory',
    };
    
    return iconMap[node.type] || 'object';
  }
};

/**
 * Message and error node behaviors
 */
export const MessageNodeBehaviors = {
  /**
   * Gets error details
   */
  getErrorDetails(node: MessageTreeNode): { message: string; isError: boolean } {
    return {
      message: node.metadata.error || 'Unknown message',
      isError: node.type === 'error'
    };
  }
};

/**
 * Columns folder behaviors
 */
export const ColumnsFolderBehaviors = {
  /**
   * Gets folder content statistics
   */
  getContentStats(node: ColumnsFolderNode): { count: number } {
    return { count: node.children?.length || 0 };
  },

  /**
   * Gets the parent table info
   */
  getTableInfo(node: ColumnsFolderNode): { schema: string; tableName: string } {
    return {
      schema: node.metadata.schema,
      tableName: node.metadata.tableName
    };
  }
};

/**
 * Table subfolder behaviors (Keys, Constraints, etc.)
 */
export const TableSubfolderBehaviors = {
  /**
   * Gets folder content statistics
   */
  getContentStats(node: TableSubfolderNode): { count: number } {
    return { count: node.children?.length || 0 };
  },

  /**
   * Checks if the folder functionality is implemented
   */
  isImplemented(node: TableSubfolderNode): boolean {
    // Only columns folder is currently implemented
    return node.type === 'columns-folder';
  },

  /**
   * Gets the parent table info
   */
  getTableInfo(node: TableSubfolderNode): { schema: string; tableName: string } {
    return {
      schema: node.metadata.schema,
      tableName: node.metadata.tableName
    };
  }
};

/**
 * Column node behaviors
 */
export const ColumnNodeBehaviors = {
  /**
   * Gets the column's display name with type info
   */
  getDisplayName(node: ColumnTreeNode): string {
    const { columnName, dataType, isPrimaryKey } = node.metadata;
    const pkIndicator = isPrimaryKey ? ' (PK)' : '';
    return `${columnName} (${dataType})${pkIndicator}`;
  },

  /**
   * Gets column info
   */
  getColumnInfo(node: ColumnTreeNode): {
    name: string;
    dataType: string;
    isNullable: boolean;
    isPrimaryKey: boolean;
  } {
    const { columnName, dataType, isNullable, isPrimaryKey } = node.metadata;
    return {
      name: columnName,
      dataType,
      isNullable,
      isPrimaryKey
    };
  },

  /**
   * Formats the column type with precision/scale if applicable.
   *
   * F1 (Soporte round 7) — `maxLength` is stored exactly as the engine
   * reports it, and SQL Server's `sys.columns.max_length` is BYTES (2 per
   * character for the N-types), so this label used to read `nvarchar(500)`
   * for an `NVARCHAR(250)` column. Pass the owning connection's engine (the
   * tree row resolves it from `node.metadata.connectionId`) and the shared
   * `displayColumnLength` rule converts it the same way the DDL surfaces do.
   * Omitting `engine` keeps the stored value — never a guess.
   */
  getFormattedType(node: ColumnTreeNode, engine?: DatabaseEngineId): string {
    const { dataType, maxLength, precision, scale } = node.metadata;

    // Handle types with max length
    if (maxLength !== undefined && isLengthDataType(dataType)) {
      const displayed = displayColumnLength(engine, dataType, maxLength);
      const lengthStr =
        maxLength === -1 ? 'max' : String(typeof displayed === 'number' ? displayed : maxLength);
      return `${dataType}(${lengthStr})`;
    }

    // Handle numeric types with precision/scale
    if (precision !== undefined && ['decimal', 'numeric'].includes(dataType.toLowerCase())) {
      return scale !== undefined && scale > 0
        ? `${dataType}(${precision},${scale})`
        : `${dataType}(${precision})`;
    }

    return dataType;
  }
};

/**
 * Generic behavior functions that work on any node type
 */
export const NodeBehaviors = {
  /**
   * Gets child count for a node
   */
  getChildCount(node: DatabaseExplorerNode): number {
    return node.children?.length || 0;
  },
  
  /**
   * Checks if a node can be expanded in the UI
   */
  isExpandable(node: DatabaseExplorerNode): boolean {
    return (node.children && node.children.length > 0) || !!node.canHaveChildren;
  },
  
  /**
   * Gets the path segments from root to this node
   */
  getNodePath(node: DatabaseExplorerNode, allNodes: DatabaseExplorerNode[]): string[] {
    const path: string[] = [node.name];
    let currentNode = node;
    
    // Walk up the tree
    while (currentNode.parentId) {
      const parent = allNodes.find(n => n.id === currentNode.parentId);
      if (!parent) break;
      
      path.unshift(parent.name);
      currentNode = parent;
    }
    
    return path;
  },
  
  /**
   * Applies the appropriate behavior functions based on node type
   */
  forNode(node: DatabaseExplorerNode) {
    if (node.type === 'connection') {
      return ConnectionNodeBehaviors;
    } else if (isDatabaseNode(node)) {
      return DatabaseNodeBehaviors;
    } else if (isDatabasesFolderNode(node)) {
      return DatabasesFolderBehaviors;
    } else if (isTablesFolderNode(node)) {
      return TablesFolderBehaviors;
    } else if (isViewsFolderNode(node)) {
      return ViewsFolderBehaviors;
    } else if (isProgrammabilityFolderNode(node)) {
      return ProgrammabilityFolderBehaviors;
    } else if (isStoredProceduresFolderNode(node)) {
      return StoredProceduresFolderBehaviors;
    } else if (isFunctionsFolderNode(node)) {
      return FunctionsFolderBehaviors;
    } else if (isColumnsFolderNode(node)) {
      return ColumnsFolderBehaviors;
    } else if (isTableSubfolderNode(node)) {
      return TableSubfolderBehaviors;
    } else if (isColumnNode(node)) {
      return ColumnNodeBehaviors;
    } else if (node.type === 'message' || node.type === 'error') {
      return MessageNodeBehaviors;
    } else {
      return DatabaseObjectNodeBehaviors;
    }
  }
}; 
